import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { RATING } from "@/constants/testIds";
import { Star } from "lucide-react";
import { toast } from "sonner";

export default function Ratings({ productId, canRate }) {
  const { user } = useAuth();
  const [data, setData] = useState({ avg: 0, count: 0, ratings: [] });
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/products/${productId}/ratings`).then(({ data }) => setData(data)), [productId]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/products/${productId}/ratings`, { stars, comment });
      setComment("");
      toast.success("Rating saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rating failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="mt-14 border-t border-white/10 pt-10">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/50">// Reviews</div>
          <h2 className="font-display font-black text-2xl tracking-tight mt-2">What buyers say</h2>
        </div>
        <div data-testid={RATING.avg} className="text-right">
          <div className="font-mono text-3xl text-[#CCFF00]">
            {data.avg?.toFixed?.(1) || "—"}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
            {data.count} rating{data.count === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {user && canRate && (
        <form data-testid={RATING.form} onSubmit={submit} className="border border-white/10 bg-[#0A0A0A] p-5 mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">Your rating</div>
          <div className="flex gap-1 mb-4">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                data-testid={RATING.starBtn(n)}
                onClick={() => setStars(n)}
                className="p-1"
              >
                <Star
                  className={`w-6 h-6 ${n <= stars ? "fill-[#CCFF00] text-[#CCFF00]" : "text-white/30"}`}
                />
              </button>
            ))}
          </div>
          <textarea
            data-testid={RATING.commentInput}
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional comment (max 500 chars)"
            className="brutal-input w-full px-3 py-2.5 mb-3"
            maxLength={500}
          />
          <button
            data-testid={RATING.submitBtn}
            disabled={busy}
            className="bg-[#FF3366] text-white font-mono uppercase tracking-[0.2em] text-xs px-4 py-2 hover:bg-[#FF3366]/90 disabled:opacity-40"
          >
            {busy ? "Saving…" : "Submit rating"}
          </button>
        </form>
      )}

      <div data-testid={RATING.list} className="space-y-3">
        {data.ratings.length === 0 ? (
          <div className="font-mono text-xs text-white/50">No reviews yet.</div>
        ) : data.ratings.map((r) => (
          <div key={r.rating_id} className="border border-white/10 bg-[#0A0A0A] p-4">
            <div className="flex items-center justify-between">
              <div className="font-mono text-xs text-white/80">{r.user_name}</div>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-3.5 h-3.5 ${n <= r.stars ? "fill-[#CCFF00] text-[#CCFF00]" : "text-white/20"}`} />
                ))}
              </div>
            </div>
            {r.comment && <p className="mt-2 text-sm text-white/80 whitespace-pre-wrap">{r.comment}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}
